import {useApolloClient} from '@apollo/client';
import {none} from '@danwang/option';
import {TransferType, useMergeTransactionsMutation} from '@transactions/graphql';
import {parseDate} from 'common/date';
import Money from 'common/Money';
import {DateTime} from 'luxon';
import * as React from 'react';
import {AmountDisplay} from 'src/AmountDisplay';
import {Button} from 'src/Button';
import DatePicker from 'src/DatePicker/DatePicker';
import InputGroup from 'src/InputGroup';
import PayeeInput from 'src/inputs/PayeeInput';
import {TagsAndFieldsInput} from 'src/inputs/TagsAndFieldsInput/TagsAndFieldsInput';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import {PaymentTransactionFields} from 'src/PaymentTransactionFields';
import {TransferTypeSelect} from 'src/TransferKindSelect';
import type {Option} from '@danwang/option';
import type {UnifiedTransactionFragment} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

export type MergeTransactionsModalUserProps = {
  transactions: UnifiedTransactionFragment[];
  onSuccess?: () => void;
};

type FormState = {
  payee: string;
  description: string;
  date: DateTime;
  category: Option<string>;
  tags: string[];
  fieldValues: Record<string, string>;
  transferType: TransferType;
};

export const MergeTransactionsModal = ({
  onRequestClose,
  transactions,
  onSuccess,
}: MergeTransactionsModalUserProps & SystemProps) => {
  const formId = React.useId();
  const client = useApolloClient();

  const netAmount = transactions.reduce((sum, tx) => sum + tx.netAmount, 0);
  const isTransfer = netAmount === 0;
  const currency = transactions[0]?.currency ?? 'USD';

  const defaultDate = React.useMemo(() => {
    if (transactions.length === 0) return DateTime.local();
    return DateTime.max(...transactions.map((tx) => parseDate(tx.date)));
  }, [transactions]);

  const [form, setForm] = React.useState<FormState>(() => {
    // A unanimous payee across the sources survives the merge as a prefill —
    // the common case is a charge and its refund from the same merchant,
    // where retyping "Amazon" would be pure friction. Any disagreement (or
    // all-empty) starts blank rather than guessing.
    const payees = [
      ...new Set(
        transactions
          .map((tx) => tx.payee)
          .filter((payee): payee is string => payee != null && payee !== ''),
      ),
    ];
    return {
      payee: payees.length === 1 ? payees[0] : '',
      description: '',
      date: defaultDate,
      category: none,
      tags: [],
      fieldValues: {},
      transferType: TransferType.AccountTransfer,
    };
  });

  const [mergeTransactions, {loading, error}] = useMergeTransactionsMutation();

  const canSubmit =
    isTransfer || (!form.category.isEmpty && form.payee.trim() !== '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    void mergeTransactions({
      variables: {
        transactionIds: transactions.map((tx) => tx._id),
        date: form.date.toISODate() ?? '',
        description: form.description,
        // A transfer's payee is its optional counterparty; empty means none.
        payee: isTransfer
          ? form.payee.trim() !== ''
            ? form.payee
            : undefined
          : form.payee,
        category: isTransfer
          ? undefined
          : form.category.isEmpty
            ? undefined
            : form.category.getOrElse(''),
        transferType: isTransfer ? form.transferType : undefined,
        tags: form.tags,
        fieldValues: form.fieldValues,
      },
    }).then((result) => {
      if (result.data) {
        onSuccess?.();
        void client.refetchQueries({include: ['Transactions']});
        onRequestClose();
      }
    });
  };

  const footer = (
    <>
      <Button variant="ghost" onClick={onRequestClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={loading || !canSubmit}
      >
        Merge
      </Button>
    </>
  );

  return (
    <Modal onRequestClose={onRequestClose} header="Merge Transactions" footer={footer}>
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="text-muted-foreground flex items-center gap-1 text-sm">
          Merging {transactions.length} transactions · net{' '}
          <AmountDisplay
            inline
            variant="plain"
            amount={Money.fromInt(netAmount, currency)}
          />
        </div>
        <div className="flex flex-col gap-2">
          {isTransfer && (
            <InputGroup label="Type">
              <TransferTypeSelect
                value={form.transferType}
                onChange={(transferType) => setForm({...form, transferType})}
              />
            </InputGroup>
          )}
          {!isTransfer && (
            <PaymentTransactionFields
              value={{
                payee: form.payee,
                category: form.category,
                description: form.description,
              }}
              onChange={({payee, category, description}) =>
                setForm({...form, payee, category, description})
              }
            />
          )}
          {isTransfer && (
            <>
              <InputGroup label="Description">
                <TextInput
                  value={form.description}
                  onChange={(description) => setForm({...form, description})}
                  placeholder="Add description"
                />
              </InputGroup>
              <InputGroup label="Payee">
                <PayeeInput
                  value={form.payee}
                  onChange={(payee) => setForm({...form, payee})}
                />
              </InputGroup>
            </>
          )}
          <InputGroup label="Date">
            <DatePicker
              selectedDate={form.date}
              onChange={(date) => setForm({...form, date})}
            />
          </InputGroup>
          <InputGroup label="Tags & Fields">
            <TagsAndFieldsInput
              tags={form.tags}
              fieldValues={form.fieldValues}
              onChange={(tags, fieldValues) =>
                setForm({...form, tags, fieldValues})
              }
            />
          </InputGroup>
        </div>
        {error && (
          <div className="text-destructive text-sm">{error.message}</div>
        )}
      </form>
    </Modal>
  );
};
